/**
 * exercise-menu-item.js
 */

import React from 'react';
import { Types } from 'utils/constants';
import { 
  handlerExerciseCompletionStatus,
  supervisorExerciseCompletionStatus 
} from 'utils/record-status';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import DrawerMenuItem from 'components/menu-drawer/menu/drawer-menu-item';
import IconPatrolDetection from 'components/icon-patrol-detection';
import CompletionStatusIcon from 'components/record-status-icons/completion';
import ReviewStatusIconExerciseHandler from 'components/record-status-icons/review/review-status-icon-exercise-handler';
import ReviewStatusIconExerciseSuper from 'components/record-status-icons/review/review-status-icon-exercise-super';
import MenuItemLabel from './menu-item-label';


export default function ExerciseMenuItem(props) {
  const {
    id,
    to,
    isActive,
    isHighlighted,
  } = props;
  const { user } = React.useContext(UserContext);
  const recordStore = useRecordStore();
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, id).ornull;
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, exerciseRecord?.get()?.eventId).ornull;

  // event
  const isOptional = eventRecord?.get()?.membership?.isOptional;
  const isAttending = eventRecord?.get()?.membership?.isAttending;

  // exercise
  const haveExercise = Boolean(exerciseRecord);
  const exerciseName = exerciseRecord && exerciseRecord.get().name;
  const workMode = exerciseRecord && exerciseRecord.get().workMode;
  const patrolIconType = exerciseRecord && exerciseRecord.get().patrol?.patrolTypes.length === 1
    ? exerciseRecord.get().patrol.patrolTypes[0].type
    : Types.Patrol.SCENARIO;

  // answers
  const answerIds = id === 0 ? [] : exerciseRecord ? exerciseRecord.get().answers : [];
  const answerRecords = recordStore.useRecordArray(Types.Record.ANSWER, answerIds);
  const stateString = JSON.stringify(
    answerRecords.map(a => 
      `${a.get().dog.appliesToExercise}-${a.get().dateModified}-${a.get().review?.status}-${a.get().isOutdated}`
    )
  );


  const dogListSummary = React.useMemo(() => {
    let incomplete = [], complete = [], na = [];

    // if too many dogs, build summary view
    if (answerRecords.length > 3) {
      answerRecords.forEach(a => {
        if (!a.get().dog.appliesToExercise) {
          na.push(a.get().dog.name);
        } else if (a.get().dateCompleted) {
          complete.push(a.get().dog.name);
        } else {
          incomplete.push(a.get().dog.name);
        }
      });
    }

    return { incomplete, complete, na };

  // answerStringified is answerRecords ... stringified!
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateString]);


  const icon = React.useMemo(() => (
    <IconPatrolDetection
      workMode={workMode}
      patrolType={patrolIconType}
    />
  ), [workMode, patrolIconType]);


  const label = React.useMemo(() => {
    if (!haveExercise || !answerRecords) return null;

    return <MenuItemLabel
      text={exerciseName}
      statusIcons={<>

        {/* HANDLER STATUS ICONS */}
        {(user.role.isHandler && exerciseRecord.get().id > 0) &&
        <>
          <ReviewStatusIconExerciseHandler
            reviewStats={exerciseRecord.get().reviewStats}
            isAvailable={exerciseRecord.get().userStats?.complete > 0}
            to={to}
            showTooltip
          />
          <CompletionStatusIcon
            params={handlerExerciseCompletionStatus(
              exerciseRecord.get().userStats?.complete,
              exerciseRecord.get().userStats?.incomplete,
              exerciseRecord.get().userStats?.outdated,
              eventRecord.get(),
              null,
              exerciseRecord.get().answers.length
            )}
            to={to}
            showTooltip
          />
        </>}

        {/* SUPERVISOR STATUS ICONS */}
        {((user.role.isSupervisor || user.role.isTrainer) && exerciseRecord.get().id > 0) &&
        <>
          {user.role.isSupervisor &&
          <ReviewStatusIconExerciseSuper
            reviewStats={exerciseRecord.get().reviewStats}
            isAvailable={exerciseRecord.get().userStats?.complete > 0}
            to={to}
            showTooltip
          />}
          <CompletionStatusIcon 
            params={supervisorExerciseCompletionStatus(
              exerciseRecord.get().userStats?.complete,
              exerciseRecord.get().userStats?.incomplete,
              eventRecord.get().date
            )}
            to={to}
            showTooltip
          />
        </>}
      </>}
    />;

  // completionsStringified is answerRecords ... stringified!
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haveExercise, exerciseName, stateString, isOptional, isAttending, dogListSummary]);


  if (!haveExercise) return null;

  return (
    <>
      <div data-testtype="exercise-menu-item">
        <DrawerMenuItem
          id={`exercise-${exerciseRecord.get().id}`}
          icon={icon}
          label={label}
          to={to}
          isActive={isActive}
          isHighlighted={isHighlighted}
        />
      </div>
    </>
  );
}